import React, { useState, useEffect } from 'react';
import { Button, Table, Space, message, Select, AutoComplete, Modal } from 'antd';

const AlertSelectionModal = ({ open, onClose, selectedIds, onSelectionChange, singleSelect = false, callAction }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [scopeFilter, setScopeFilter] = useState(undefined);
  const [typeFilter, setTypeFilter] = useState(undefined);
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(selectedIds || []);

  useEffect(() => {
    setSelectedRowKeys(selectedIds || []);
  }, [selectedIds, open]);

  const loadData = async (opts = {}) => {
    setLoading(true);
    try {
      const params = {
        page: opts.page || page,
        page_size: opts.pageSize || pageSize,
        status: 1,
        eligible_only: 1,
        debug: 1,
        eligible_mode: opts.eligibleMode || 'all',
      };
      if (scopeFilter) params.scope = scopeFilter;
      if (typeFilter) params.alert_type = typeFilter;
      if (search) params.q = search;
      // eslint-disable-next-line no-console
      console.log('AlertSelectionModal.loadData params:', params);
      const env = await callAction('list_alert_definitions', params);
      const data = (env && env.data) || {};
      // eslint-disable-next-line no-console
      console.log('AlertSelectionModal.loadData response:', { total: data.total, page: data.page, items_len: (data.items || []).length, debug: data.debug });
      if ((data.items || []).length === 0) {
        const dbg = data.debug || {};
        const reason = dbg.allowed_fields_count === 0
          ? 'No fundamentals columns visible from correlation_research (allowed_fields_count=0).'
          : 'No alerts match filters or eligibility.';
        // eslint-disable-next-line no-console
        console.warn('AlertSelectionModal empty result. Debug:', dbg);
        message.warning(reason);
        if (!opts.fallback) {
          // Try a softer eligibility: any field present instead of all
          console.warn('Retrying with eligible_mode=any …');
          await loadData({ page: 1, pageSize, eligibleMode: 'any', fallback: true });
          return;
        }
      }
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || 10);
    } catch (e) {
      message.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadData({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeFilter, typeFilter, search]);

  const onCheckAll = () => {
    const pageIds = rows.map(r => r.ID);
    const merged = Array.from(new Set([...(selectedRowKeys || []), ...pageIds]));
    setSelectedRowKeys(merged);
    onSelectionChange(merged);
  };

  const onUncheckAll = () => {
    const pageIdSet = new Set(rows.map(r => r.ID));
    const remaining = (selectedRowKeys || []).filter(id => !pageIdSet.has(id));
    setSelectedRowKeys(remaining);
    onSelectionChange(remaining);
  };

  const fetchSuggestions = async (q) => {
    setSearch(q);
    try {
      if (!q) {
        setSuggestions([]);
        return;
      }
      const env = await callAction('alert_definitions_suggest', { q });
      const list = (env && env.data && env.data.suggestions) || [];
      setSuggestions(list.map(s => ({ value: s })));
    } catch {
      setSuggestions([]);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'ID' },
    { title: 'Scope', dataIndex: 'scope' },
    { title: 'Type', dataIndex: 'alert_type' },
    { title: 'Subtype', dataIndex: 'alert_subtype' },
    { title: 'Status', dataIndex: 'status' },
  ];

  return (
    <Modal
      title="Select Alerts"
      open={open}
      onCancel={onClose}
      footer={<Button type="primary" onClick={onClose}>Close</Button>}
      width={900}
    >
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear
          placeholder="Filter scope"
          style={{ width: 180 }}
          value={scopeFilter}
          onChange={setScopeFilter}
          options={[
            { label: 'Portfolio', value: 'Portfolio' },
            { label: 'Favorites', value: 'Favorites' },
            { label: 'Unknown', value: 'Unknown' },
          ]}
        />
        <Select
          allowClear
          placeholder="Filter alert type"
          style={{ width: 220 }}
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            { label: 'accumulate', value: 'accumulate' },
            { label: 'reduce', value: 'reduce' },
            { label: 'other', value: 'other' },
          ]}
        />
        <AutoComplete
          style={{ width: 260 }}
          options={suggestions}
          onSearch={fetchSuggestions}
          onSelect={(val) => setSearch(String(val))}
          placeholder="Search ID/type/subtype"
          allowClear
        />
        {!singleSelect && (
          <>
            <Button onClick={onCheckAll}>Check all (page)</Button>
            <Button onClick={onUncheckAll}>Uncheck all (page)</Button>
          </>
        )}
      </Space>
      <Table
        size="small"
        rowKey="ID"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => loadData({ page: p, pageSize: ps }),
        }}
        rowSelection={{
          type: singleSelect ? 'radio' : 'checkbox',
          selectedRowKeys: singleSelect ? (selectedRowKeys.slice(0, 1)) : selectedRowKeys,
          onChange: (keys) => {
            const newKeys = singleSelect ? keys.slice(-1) : keys;
            setSelectedRowKeys(newKeys);
            onSelectionChange(newKeys);
          },
        }}
      />
    </Modal>
  );
};

export default AlertSelectionModal;

